/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { ConfluenceIcon } from '@atlaskit/logo/confluence/icon';
import { JiraIcon } from '@atlaskit/logo/jira/icon';
import { LoomIcon } from '@atlaskit/logo/loom/icon';
import type { LogoProps } from '@atlaskit/logo/types';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { GithubIcon } from '@atlassian/logo-third-party/github';
import { GoogleCalendarIcon } from '@atlassian/logo-third-party/google-calendar';
import { GoogleDriveIcon } from '@atlassian/logo-third-party/google-drive';
import { MicrosoftSharePointIcon } from '@atlassian/logo-third-party/microsoft-sharepoint';
import { SlackIcon } from '@atlassian/logo-third-party/slack';
import type { ThirdPartyLogoProps } from '@atlassian/logo-third-party/types';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { InsightSummaryApp } from '../../types';

export const MAX_VISIBLE_APP_ICONS = 6;

const ICON_ROTATIONS = [0, -6, 4] as const;

const messages = defineMessages({
	overflowCount: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.app-icon-stack.AppIconStack.overflowCount',
		defaultMessage: '+{count}',
		description: 'Overflow indicator for additional source apps in the app icon stack.',
	},
});

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
	},
	inlineContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		position: 'relative',
		marginInlineStart: token('space.075'),
		verticalAlign: 'middle',
	},
	item: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
	},
	itemFirst: {
		marginLeft: token('space.0', '0px'),
	},
	itemRest: {
		marginLeft: token('space.negative.050', '-4px'),
	},
	tile: {
		width: '20px',
		height: '20px',
		borderRadius: token('radius.tile'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		position: 'relative',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	filledTile: {
		backgroundColor: 'transparent',
		borderColor: 'transparent',
	},
	overflowTile: {
		// minWidth (not fixed width) so multi-digit counts grow into a pill, not clip.
		minWidth: '20px',
		height: '20px',
		borderRadius: token('radius.tile'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingInline: token('space.050'),
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
});

type AtlassianLogoComponent = (props: LogoProps) => JSX.Element;
type ThirdPartyLogoComponent = (props: ThirdPartyLogoProps) => JSX.Element;
type ResolvedAppLogo = {
	app: InsightSummaryApp;
	node: JSX.Element;
};

const ATLASSIAN_LOGOS_BY_APP_ID: Record<string, AtlassianLogoComponent> = {
	confluence: ConfluenceIcon,
	jira: JiraIcon,
	loom: LoomIcon,
};

const THIRD_PARTY_LOGOS_BY_APP_ID: Record<string, ThirdPartyLogoComponent> = {
	github: GithubIcon,
	'google-calendar': GoogleCalendarIcon,
	'google-drive': GoogleDriveIcon,
	'microsoft-sharepoint': MicrosoftSharePointIcon,
	slack: SlackIcon,
};

const SOURCE_APP_ID_TO_LOGO_ID: Record<string, string> = {
	drive: 'google-drive',
	gcal: 'google-calendar',
	sharepoint: 'microsoft-sharepoint',
};

const normalizeAppId = (appId: string): string => appId.trim().toLowerCase().replace(/_/g, '-');

const getLogoId = (appId: string): string => {
	const normalizedAppId = normalizeAppId(appId);
	return SOURCE_APP_ID_TO_LOGO_ID[normalizedAppId] ?? normalizedAppId;
};

const resolveAppLogo = (app: InsightSummaryApp): ResolvedAppLogo | null => {
	const logoId = getLogoId(app.id);
	const testId = `app-logo-${app.id}`;
	const AtlassianLogo = ATLASSIAN_LOGOS_BY_APP_ID[logoId];

	if (AtlassianLogo) {
		return {
			app,
			node: (
				<Box as="span" xcss={cx(styles.tile, styles.filledTile)}>
					<AtlassianLogo label="" size="xsmall" testId={testId} />
				</Box>
			),
		};
	}

	const ThirdPartyLogo = THIRD_PARTY_LOGOS_BY_APP_ID[logoId];

	if (ThirdPartyLogo) {
		return {
			app,
			node: <ThirdPartyLogo label="" size="xsmall" testId={testId} />,
		};
	}

	return null;
};

export interface AppIconStackProps {
	sourceApps: InsightSummaryApp[];
	appCount?: number;
	appearance?: 'block' | 'inline';
	testId?: string;
}

export const AppIconStack = ({
	sourceApps,
	appCount,
	appearance = 'block',
	testId,
}: AppIconStackProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (sourceApps.length === 0) {
		return null;
	}

	const totalCount = appCount ?? sourceApps.length;
	const visibleLogos = sourceApps
		.slice(0, totalCount)
		.map(resolveAppLogo)
		.filter((logo): logo is ResolvedAppLogo => logo !== null)
		.slice(0, MAX_VISIBLE_APP_ICONS);
	const overflowCount = Math.max(totalCount - visibleLogos.length, 0);

	if (visibleLogos.length === 0 && overflowCount === 0) {
		return null;
	}

	const isInline = appearance === 'inline';
	const formattedOverflowCount = formatMessage(messages.overflowCount, { count: overflowCount });

	return (
		<Box
			as={isInline ? 'span' : undefined}
			xcss={isInline ? styles.inlineContainer : styles.container}
			testId={testId}
			aria-hidden="true"
		>
			{visibleLogos.map(({ app, node }, index) => (
				<Box
					as="span"
					key={`${app.id}-${index}`}
					xcss={cx(styles.item, index === 0 ? styles.itemFirst : styles.itemRest)}
					style={{
						zIndex: index,
						transform: `rotate(${ICON_ROTATIONS[index % ICON_ROTATIONS.length]}deg)`,
					}}
				>
					{node}
				</Box>
			))}
			{overflowCount > 0 ? (
				<Box
					as="span"
					xcss={cx(styles.item, visibleLogos.length === 0 ? styles.itemFirst : styles.itemRest)}
					style={{
						zIndex: visibleLogos.length,
						transform: `rotate(${ICON_ROTATIONS[visibleLogos.length % ICON_ROTATIONS.length]}deg)`,
					}}
				>
					<Box
						as="span"
						xcss={styles.overflowTile}
						testId={testId ? `${testId}-overflow` : undefined}
					>
						<Text size="small" color="color.text.subtle">
							{formattedOverflowCount}
						</Text>
					</Box>
				</Box>
			) : null}
		</Box>
	);
};
